<template>
    <div class="loader">
        <svg class="wheel" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
            <defs>
                <!-- A clip region is the union of its children, so seam A is "the first petal or the second". -->
                <clipPath :id="`${uid}-seam-a`">
                    <circle ref="seamAFirst" />
                    <circle ref="seamASecond" />
                </clipPath>
                <clipPath :id="`${uid}-seam-b`">
                    <circle ref="seamBFirst" />
                </clipPath>
            </defs>

            <g ref="ring" class="ring">
                <!-- Painted last petal first, so a lower index sits on top of its neighbour
                     and the overlap shingles the same way the whole way round. -->
                <circle v-for="petal in PAINT_ORDER" :key="petal.code" ref="petalEls" :r="PETAL_RADIUS" :fill="petal.color" />

                <!-- Painter's order makes the ring a spiral, so the shingling breaks where its two ends
                     meet: the first petal wrongly covers the last two, and the second covers the last.
                     These clipped copies redraw those two petals over exactly the discs they belong on,
                     leaving every other overlap untouched. The lower index goes on top. -->
                <g ref="lastGhost" :clip-path="`url(#${uid}-seam-a)`"><circle ref="lastGhostDisc" :r="PETAL_RADIUS" :fill="LAST_PETAL.color" /></g>
                <g ref="prevGhost" :clip-path="`url(#${uid}-seam-b)`"><circle ref="prevGhostDisc" :r="PETAL_RADIUS" :fill="PREV_PETAL.color" /></g>
            </g>

            <circle ref="core" class="core" :cx="CENTRE_X" :cy="CENTRE_Y" :r="CORE_RADIUS" />
        </svg>

        <p class="label" :class="{ 'is-open': open }" aria-hidden="true">
            <span class="dot" :style="dotStyle"></span>
            <span class="name">{{ name }}</span>
        </p>
    </div>
</template>

<script setup lang="ts">
/**
 * The wheel is pushed once and friction takes it down onto the archetype the assessment produced, so the
 * coloured core it ends on is the medallion the result screen opens with.
 *
 * Two things force a per-frame JS timeline rather than CSS. The wheel cannot land on an answer it does not
 * have yet, so the timeline holds just after the petals are out and coasts until `land` arrives; and a
 * backgrounded tab leaves CSS transitions stuck, which here would strand the user mid-flow rather than
 * merely pause a decoration.
 *
 * Colours and ordering come from `perspectiveTypes` so the wheel and the results page cannot drift apart.
 */
import { PERSPECTIVE_TYPES, TYPE_COLORS, WHEEL_ORDER } from "~vue/pages/perspectiveTypes";
import { onBeforeUnmount, onMounted, ref, useId, useTemplateRef, watch } from "vue";

const { land = null } = defineProps<{
    /** Four-letter type to land on, or null while the result is still being fetched. */
    land?: string | null;
}>();

const emit = defineEmits<{ landed: [] }>();

const CENTRE_X = 200;
const CENTRE_Y = 206;
const RING_RADIUS = 126;
const PETAL_RADIUS = 56;
const CORE_RADIUS = 88;

/* The seam copies are clipped a hair wide of the disc they sit on. The upper one is wider again, so it
   covers the lower one's antialiased edge rather than blending with it and letting a sliver of the wrong
   colour through. */
const CLIP_PAD = 0.6;
const CLIP_PAD_TOP = 1.6;

/* Timeline, in ms from the start of the run. */
const EXPAND_MS = 800; // petals travelling out to the ring
const FADE_MS = 400; // petals fading up
/* One push, then friction the whole way down — a constant-speed stretch reads as a loop rather than as
   something a hand set going. */
const SPIN_MS = 4800; // from the push to standing still
const SPIN_V0 = 700; // deg/s at the moment of the push
const SPIN_EXP = 1.7; // how hard friction bites; higher fades quicker
const FAST_TICK = 80; // name cycling speed while the wheel is still quick
const SLOW_AT = 2500; // the name starts its slowing sequence here
const SLOW_OFFSETS = [0, 120, 280, 500, 800, 1200, 1700];
const FINAL_AT = SLOW_AT + SLOW_OFFSETS[SLOW_OFFSETS.length - 1];
const CONVERGE_AT = FINAL_AT + 400;
const CONVERGE_MS = 620; // petals accelerating back into the centre
const CONVERGE_SHRINK = 0.72;
const POP_MS = 440; // the core's kick as the colour is taken
const CORE_PEAK = 1.1;
const CORE_SETTLE = 1.04;
/* The petals are all out by here, and the ring is still turning fast enough to read as a spin. */
const HOLD_AT = EXPAND_MS;
/* Let the landed core stand on its own before the result screen takes over from it. */
const REST_MS = 400;

interface Petal {
    code: string;
    name: string;
    color: string;
    cx: number;
    cy: number;
}

const PETALS: Petal[] = WHEEL_ORDER.map((code, index) => {
    const angle = ((-90 + (index * 360) / WHEEL_ORDER.length) * Math.PI) / 180;
    return {
        code,
        name: PERSPECTIVE_TYPES[code]?.title ?? code,
        color: TYPE_COLORS[code],
        cx: CENTRE_X + RING_RADIUS * Math.cos(angle),
        cy: CENTRE_Y + RING_RADIUS * Math.sin(angle),
    };
});

const PAINT_ORDER = [...PETALS].reverse();
const LAST_PETAL = PETALS[PETALS.length - 1];
const PREV_PETAL = PETALS[PETALS.length - 2];

const clamp01 = (value: number): number => Math.min(Math.max(value, 0), 1);
const easeOutCubic = (value: number): number => 1 - (1 - value) ** 3;

/** The CSS "standard" curve, solved numerically so the timeline moves the way a CSS transition would. */
function cubicBezier(p1x: number, p1y: number, p2x: number, p2y: number): (x: number) => number {
    const a = (x1: number, x2: number): number => 1 - 3 * x2 + 3 * x1;
    const b = (x1: number, x2: number): number => 3 * x2 - 6 * x1;
    const c = (x1: number): number => 3 * x1;
    const calc = (t: number, x1: number, x2: number): number => ((a(x1, x2) * t + b(x1, x2)) * t + c(x1)) * t;
    const slope = (t: number, x1: number, x2: number): number => 3 * a(x1, x2) * t * t + 2 * b(x1, x2) * t + c(x1);
    return (x: number): number => {
        if (x <= 0) {
            return 0;
        }
        if (x >= 1) {
            return 1;
        }
        let t = x;
        for (let i = 0; i < 6; i++) {
            const s = slope(t, p1x, p2x);
            if (s === 0) {
                break;
            }
            t -= (calc(t, p1x, p2x) - x) / s;
        }
        return calc(t, p1y, p2y);
    };
}

const standard = cubicBezier(0.4, 0, 0.2, 1);
/* The way back in accelerates, so the petals are pulled home rather than drifting; that is what makes the
   arrival read as a snap. */
const snapIn = cubicBezier(0.6, 0, 0.9, 0.4);

function solveFor(ease: (x: number) => number, target: number): number {
    let low = 0;
    let high = 1;
    for (let i = 0; i < 24; i++) {
        const mid = (low + high) / 2;
        if (ease(mid) < target) {
            low = mid;
        } else {
            high = mid;
        }
    }
    return (low + high) / 2;
}

/* A petal is out of sight once it has slipped entirely behind the core. Solving for that moment, rather
   than guessing a delay, welds the colour change to the instant the last of them lands. */
const HIDDEN_AT_EASED = (RING_RADIUS + PETAL_RADIUS - CORE_RADIUS) / (RING_RADIUS + CONVERGE_SHRINK * PETAL_RADIUS);
const SNAP_AT = CONVERGE_AT + CONVERGE_MS * solveFor(snapIn, HIDDEN_AT_EASED);
const END_AT = Math.max(SNAP_AT + POP_MS + 160, CONVERGE_AT + CONVERGE_MS);
const DONE_AT = END_AT + REST_MS;

const uid = useId();
const ring = useTemplateRef<SVGGElement>("ring");
const core = useTemplateRef<SVGCircleElement>("core");
const petalEls = useTemplateRef<SVGCircleElement[]>("petalEls");
const seamAFirst = useTemplateRef<SVGCircleElement>("seamAFirst");
const seamASecond = useTemplateRef<SVGCircleElement>("seamASecond");
const seamBFirst = useTemplateRef<SVGCircleElement>("seamBFirst");
const lastGhost = useTemplateRef<SVGGElement>("lastGhost");
const lastGhostDisc = useTemplateRef<SVGCircleElement>("lastGhostDisc");
const prevGhost = useTemplateRef<SVGGElement>("prevGhost");
const prevGhostDisc = useTemplateRef<SVGCircleElement>("prevGhostDisc");

const open = ref(false);
const name = ref("");
const dotStyle = ref<{ backgroundColor?: string }>({});

/** `out` is 0 for a petal parked behind the core and 1 for its place on the ring. */
function place(el: SVGCircleElement | null, petal: Petal, out: number, scale: number, pad = 0): void {
    if (el === null) {
        return;
    }
    el.setAttribute("cx", (CENTRE_X + (petal.cx - CENTRE_X) * out).toFixed(2));
    el.setAttribute("cy", (CENTRE_Y + (petal.cy - CENTRE_Y) * out).toFixed(2));
    el.setAttribute("r", (PETAL_RADIUS * scale + pad).toFixed(2));
}

function setAll(out: number, scale: number, opacity: number): void {
    petalEls.value?.forEach((el, index) => {
        place(el, PAINT_ORDER[index], out, scale);
        el.setAttribute("opacity", opacity.toFixed(3));
    });

    /* Keep the seam repairs glued to the petals they stand in for. */
    place(seamAFirst.value, PETALS[0], out, scale, CLIP_PAD);
    place(seamASecond.value, PETALS[1], out, scale, CLIP_PAD);
    place(seamBFirst.value, PETALS[0], out, scale, CLIP_PAD_TOP);
    place(lastGhostDisc.value, LAST_PETAL, out, scale);
    place(prevGhostDisc.value, PREV_PETAL, out, scale);
    lastGhost.value?.setAttribute("opacity", opacity.toFixed(3));
    prevGhost.value?.setAttribute("opacity", opacity.toFixed(3));
}

function showName(petal: Petal): void {
    name.value = petal.name;
    dotStyle.value = { backgroundColor: petal.color };
}

interface Run {
    target: Petal;
    /** The names the wheel passes through on its way down, ending on the target. */
    slowSeq: Petal[];
}

/**
 * Whether the caller has an answer for us, which is a different question from whether it is one the wheel
 * knows. `land` is typed as a plain string because it comes from a generated API model, so an unplaceable
 * value has to be handled here rather than at the call sites.
 */
function isAnswered(letters: string | null): letters is string {
    return letters !== null && letters.trim() !== "";
}

function resolveTarget(letters: string | null): Petal | null {
    if (!isAnswered(letters)) {
        return null;
    }
    const key = letters.trim().toLowerCase();
    return PETALS.find((petal) => petal.code.toLowerCase() === key || petal.name.toLowerCase() === key) ?? null;
}

function prepare(target: Petal): Run {
    const pool = PETALS.filter((petal) => petal.code !== target.code);
    for (let index = pool.length - 1; index > 0; index--) {
        const swap = Math.floor(Math.random() * (index + 1));
        [pool[index], pool[swap]] = [pool[swap], pool[index]];
    }
    return { target, slowSeq: [...pool.slice(0, SLOW_OFFSETS.length - 1), target] };
}

/** Velocity starts at the push and decays, so the ring coasts at whatever speed `t` is being held at. */
function velocityAt(t: number): number {
    return SPIN_V0 * (1 - clamp01(t / SPIN_MS)) ** SPIN_EXP;
}

/**
 * Render the loader at timeline position `t`. Pure in its arguments — no accumulated state — so it looks
 * the same however coarsely the clock is ticking.
 *
 * The name cycles on `elapsed` rather than `t` so it keeps moving while the timeline is held.
 */
function paint(t: number, angle: number, elapsed: number, run: Run | null): void {
    if (t < CONVERGE_AT) {
        setAll(standard(clamp01(t / EXPAND_MS)), 1, clamp01(t / FADE_MS));
    } else {
        /* No fade on the way back: they stay solid and disappear by sliding under the core, which reads as
           being drawn in rather than dissolving. */
        const collapsed = snapIn(clamp01((t - CONVERGE_AT) / CONVERGE_MS));
        setAll(1 - collapsed, 1 - CONVERGE_SHRINK * collapsed, 1);
    }

    ring.value?.style.setProperty("transform", `rotate(${angle.toFixed(2)}deg)`);

    if (run === null || t < SLOW_AT) {
        showName(PETALS[Math.floor(Math.max(elapsed, 0) / FAST_TICK) % PETALS.length]);
    } else {
        let stage = 0;
        while (stage + 1 < SLOW_OFFSETS.length && t >= SLOW_AT + SLOW_OFFSETS[stage + 1]) {
            stage++;
        }
        showName(run.slowSeq[stage]);
    }

    open.value = t > FADE_MS;

    const landedColor = run !== null && t >= SNAP_AT ? run.target.color : "";
    core.value?.style.setProperty("fill", landedColor);

    /* Kick out fast on arrival, then settle a little larger than it started. */
    let grow = 1;
    if (run !== null && t >= SNAP_AT) {
        const progress = clamp01((t - SNAP_AT) / POP_MS);
        const up = easeOutCubic(clamp01(progress / 0.28));
        const down = standard(clamp01((progress - 0.28) / 0.72));
        grow = 1 + (CORE_PEAK - 1) * up - (CORE_PEAK - CORE_SETTLE) * down;
    }
    core.value?.style.setProperty("transform", `scale(${grow.toFixed(4)})`);
}

let rafId: number | null = null;
let heartbeat: ReturnType<typeof setInterval> | null = null;
let lastNow: number | null = null;
let elapsed = 0;
let timeline = 0;
let angle = 0;
let run: Run | null = null;
let finished = false;

function stopDriver(): void {
    if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
    if (heartbeat !== null) {
        clearInterval(heartbeat);
        heartbeat = null;
    }
}

function finish(): void {
    if (finished) {
        return;
    }
    finished = true;
    stopDriver();
    /* The petals are behind the core by now; clearing them keeps a resized viewport from exposing one. */
    setAll(0, 1 - CONVERGE_SHRINK, 0);
    emit("landed");
}

/**
 * Advance the timeline to `now`. Both drivers share `lastNow`, so a frame and a heartbeat landing together
 * split one interval between them rather than double-counting it.
 */
function step(now: number): void {
    if (finished) {
        return;
    }
    const delta = lastNow === null ? 0 : now - lastNow;
    lastNow = now;
    elapsed += delta;

    if (run === null) {
        const target = resolveTarget(land);
        if (target !== null) {
            run = prepare(target);
        } else if (isAnswered(land)) {
            /* An answer arrived that the wheel cannot place. Holding for a better one would wedge the
               flow, since every caller waits on `landed` to move on, so drop the reveal and let the
               result screen — which reads the result itself, not our colour — take over. */
            finish();
            return;
        }
    }

    /* The wheel cannot land on an answer it does not have, so hold the timeline and let it coast. */
    timeline = Math.min(timeline + delta, run === null ? HOLD_AT : DONE_AT);
    angle += velocityAt(timeline) * (delta / 1000);

    paint(timeline, angle, elapsed, run);

    if (run !== null && timeline >= DONE_AT) {
        finish();
    }
}

/**
 * A backgrounded tab stops delivering animation frames, which would leave the flow waiting on a landing
 * that never comes. The heartbeat is throttled too, but it keeps running, so the timeline still finishes.
 */
function startDriver(): void {
    const onFrame = (now: number): void => {
        step(now);
        rafId = finished ? null : requestAnimationFrame(onFrame);
    };
    rafId = requestAnimationFrame(onFrame);
    heartbeat = setInterval(() => {
        step(performance.now());
    }, 250);
}

const prefersReducedMotion = (): boolean => globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Reduced motion gets the same two states the animation moves between, and neither of the moves. */
function settleWithoutMotion(): void {
    const target = resolveTarget(land);
    if (target === null) {
        if (isAnswered(land)) {
            finish();
            return;
        }
        setAll(1, 1, 1);
        open.value = false;
        return;
    }
    run = prepare(target);
    paint(DONE_AT, 0, DONE_AT, run);
    finish();
}

onMounted(() => {
    setAll(0, 1, 0);
    if (prefersReducedMotion()) {
        settleWithoutMotion();
        return;
    }
    startDriver();
});

watch(
    () => land,
    () => {
        if (!finished && prefersReducedMotion()) {
            settleWithoutMotion();
        }
    },
);

onBeforeUnmount(stopDriver);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .loader {
        @apply flex flex-col items-center;
    }

    .wheel {
        @apply block w-[320px] max-w-full;
    }

    .core {
        fill: var(--color-surface-raised);
    }

    /* Both turn and grow about the wheel's centre, not the SVG origin. */
    .ring,
    .core {
        transform-box: view-box;
        transform-origin: 200px 206px;
    }

    /* Held until the petals have faded up, so the name does not appear before the wheel it belongs to. */
    .label {
        @apply mt-6 flex h-8 items-center justify-center gap-3 opacity-0 transition-opacity duration-300;
    }

    .label.is-open {
        @apply opacity-100;
    }

    .dot {
        @apply size-2.5 shrink-0 rounded-full bg-border-strong;
    }

    /* A cycling type name is a machine string, so scaffold rather than serif. */
    .name {
        @apply scaffold-body-bold whitespace-nowrap text-ink;
    }
}

@media (prefers-reduced-motion: reduce) {
    .label {
        @apply transition-none;
    }
}
</style>
